<script setup lang="ts">
import { formatText } from "~/helpers/text";
import type { Portfolio } from "~/typings/portfolio";
import { computed, ref } from "vue";
import { useViewport } from "~/composables/useViewport";
import ExperienceDatetimeLocation from "~/components/pages/Projects/ExperienceCard/ExperienceDatetimeLocation/index.vue";
import ExperienceCompanyClient from "~/components/pages/Projects/ExperienceCard/ExperienceCompanyClient/index.vue";
import TimelineExperienceIndicators from "./TimelineExperienceIndicators.vue";
import defaultAvatarImg from "./default-avatar.png";

/**
 * TimelineExperienceCard - Decorates base ExperienceCard for timeline display
 * Adds hover expansion, absolute positioning, and timeline-specific behaviors
 */
interface TimelineExperienceCardProps
  extends Omit<Portfolio.Experience, "avatar"> {
  /**
   * Index in timeline for positioning (even = left, odd = right)
   */
  index: number;
  avatar?: string | null;
}

const props = withDefaults(defineProps<TimelineExperienceCardProps>(), {
  avatar: defaultAvatarImg,
});

const emit = defineEmits<{
  click: [];
}>();

const roleName = computed(() => props.roleName);

// Format description with rich text support (line breaks, basic HTML)
const formattedDescription = computed(() =>
  formatText(props.shortDescription || props.description || "")
);

// Track hover state for expansion effect (large screens only)
const isHovered = ref(false);

const hasDescription = computed(() => Boolean(props.description?.length));
const { isSmallScreen } = useViewport();

// Determine card side based on index (even = left, odd = right)
const isLeftSide = computed(() => props.index % 2 === 0);

const handleClick = (event: Event) => {
  if (isSmallScreen.value) {
    // On mobile, disallow click navigation and leave the "View More" button the only way to open details
    return;
  }

  const target = event.target as HTMLElement;
  if (target.closest("button")) {
    return;
  }

  emit("click");
};
</script>

<template>
  <UiCard
    tag="article"
    rounded="lg"
    class="timeline-experience-card relative hover:border-primary-400 dark:hover:border-primary-600"
    :class="{
      'is-expanded': isHovered,
      'is-left-side': isLeftSide,
    }"
    :aria-label="`Experience: ${company} - ${roleName}. Click for more details`"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="card-header">
      <div class="flex justify-between gap-x-2">
        <div class="flex-1 min-w-0 flex flex-col gap-2">
          <div class="relative flex flex-col gap-1">
            <UiTitle level="3" class="text-xl md:text-2xl font-bold">
              {{ roleName }}
            </UiTitle>
          </div>
          <div class="hidden lg:block">
            <ExperienceDatetimeLocation
              :start-date="startDate"
              :end-date="endDate"
              :location="location"
            />
          </div>
          <UiParagraph
            variant="soft"
            class="text-sm leading-tight transition-opacity duration-[var(--card-transition-duration)]"
            :class="{ 'opacity-0 absolute': isHovered }"
          >
            {{ client }}
          </UiParagraph>
        </div>

        <TimelineExperienceIndicators :experience="props" />
      </div>
    </div>

    <div class="card-expanded-content">
      <div class="expanded-inner mt-2">
        <div class="mb-2 block lg:hidden">
          <ExperienceDatetimeLocation
            :start-date="startDate"
            :end-date="endDate"
            :location="location"
          />
        </div>

        <div class="mb-4">
          <ExperienceCompanyClient :company="company" :client="client" />
        </div>

        <div
          v-dompurify-html="formattedDescription"
          class="prose dark:prose-invert max-w-none text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6"
        ></div>

        <ul
          v-if="projectTags && projectTags.length > 0"
          class="flex flex-wrap gap-2 mb-2"
          role="list"
          aria-label="Project categories"
        >
          <UiTag v-for="tag in projectTags" :key="tag" class="capitalize">
            {{ tag.replace(/-/g, " ") }}
          </UiTag>
        </ul>

        <UiButton
          v-if="hasDescription"
          variant="solid"
          color="secondary"
          size="md"
          class="mt-4 lg:hidden w-full text-center"
          aria-label="View more details about this experience"
          @click="emit('click')"
        >
          <span class="w-full">View More</span>
        </UiButton>
      </div>
    </div>
  </UiCard>
</template>

<style scoped>
.timeline-experience-card {
  --card-transition-duration: 0.5s;

  cursor: pointer;
  overflow: visible; /* Allow accent bar to render outside */
  transition:
    border-color 0.3s ease-out,
    border-radius var(--card-transition-duration) ease-out;
  border-radius: 0.75rem; /* Default: rounded on all corners */
}

/* Expanded state: flatten side where accent bar is */
/* Right side cards (default): flatten left side on expansion */
.timeline-experience-card.is-expanded {
  border-radius: 0 0.75rem 0.75rem 0;
}

/* Left side cards: flatten right side on expansion */
.timeline-experience-card.is-left-side.is-expanded {
  border-radius: 0.75rem 0 0 0.75rem;
}

/* Large screens: always absolutely positioned to prevent layout shift */
@media (min-width: 1024px) {
  .timeline-experience-card {
    width: 100%;
    /* Always absolute and centered on timeline dot */
    position: absolute;
    /* Align with timeline-dot (32px) */
    top: 3rem;
    /* Center around dot */
    transform: translateY(-50%);
    left: 0;
    right: 0;
    z-index: 1;
    transition: z-index var(--card-transition-duration) ease-out;
  }

  /* Expanded state on large screens */
  .timeline-experience-card.is-expanded {
    z-index: 20;
  }
}

/* Card header - always visible */
.card-header {
  position: relative;
  z-index: 1;
}

/* Expanded content - visible on mobile, hidden on large screens */
.card-expanded-content {
  overflow: hidden;
}

/* Mobile: always show expanded content */
.card-expanded-content {
  max-height: none;
  opacity: 1;
}

/* Large screens: hide by default, show on expansion */
@media (min-width: 1024px) {
  .card-expanded-content {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
    transition:
      max-height var(--card-transition-duration) ease-out,
      opacity var(--card-transition-duration) ease-out,
      margin-top var(--card-transition-duration) ease-out;
  }

  .timeline-experience-card.is-expanded .card-expanded-content {
    max-height: 2000px;
    opacity: 1;
    margin-top: 0rem;
  }
}

/* Accent bar - positioned on the side facing timeline center */
.timeline-experience-card::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0.25rem;
  opacity: 0;
  transition: opacity var(--card-transition-duration) ease-out;
  background-image: linear-gradient(
    to bottom,
    var(--color-primary-500),
    var(--color-primary-700)
  );
  pointer-events: none; /* Don't interfere with interactions */
}

/* Right side cards (odd index): accent bar on left, outside the card box */
.timeline-experience-card::before {
  left: -0.25rem; /* Position outside to prevent content push */
  border-radius: 0.75rem 0 0 0.75rem; /* Rounded on left (outer), flat on right (touches card) */
}

/* Left side cards (even index): accent bar on right, outside the card box */
.timeline-experience-card.is-left-side::before {
  left: auto;
  right: -0.25rem; /* Position outside to prevent content push */
  border-radius: 0 0.75rem 0.75rem 0; /* Rounded on right (outer), flat on left (touches card) */
}

@media (max-width: 767px) {
  .timeline-experience-card::before {
    display: none;
  }
}

.timeline-experience-card:hover::before {
  opacity: 1;
}
</style>
